// agentChatHooks.ts
import { useEffect, useRef, useCallback, useState, Dispatch, SetStateAction, RefObject } from 'react';
import useApi from '../api';
import { useTimeoutFn } from 'react-use';
import { v4 as uuidv4 } from 'uuid';
import { loadFont } from './fontLoader';


// hooks for Agent.tsx
export function useFormFields(
  conversationId: string | null,
  updateFormFields: boolean,
  initialFields: any[] = []
): [any[], Dispatch<SetStateAction<any[]>>] {
  const { post } = useApi();
  const [formFields, setFormFields] = useState(initialFields);

  useEffect(() => {
    const fetchFormFields = async () => {
      if (!conversationId) return;
      try {
        const { data } = await post(`/sessions/${conversationId}/get-form-fields/`, {});
        setFormFields(data.form_fields || []);
      } catch (error) {
        console.error('Error fetching form field values:', error);
      }
    };

    if (conversationId && (updateFormFields || formFields.length === 0)) {
      fetchFormFields();
    }
  }, [conversationId, updateFormFields, formFields.length, post]);

  return [formFields, setFormFields];
}

export function useInactivityTimer(timeoutDuration: number, onTimeout: () => void) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      onTimeout();
    }, timeoutDuration);
  }, [timeoutDuration, onTimeout]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return resetTimer;
}

export function useAutoScroll(
  shouldScroll: boolean,
  chatRef: RefObject<HTMLDivElement>,
  onScrollComplete?: () => void
) {
  useEffect(() => {
    if (shouldScroll && chatRef.current) {
      setTimeout(() => {
        chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
        if (onScrollComplete) onScrollComplete();
      }, 50);
    }
  }, [shouldScroll, chatRef, onScrollComplete]);
}

export function useAgentConfig(embedConfig: any, agentId?: string, draftAgentId?: string) {
  const { get } = useApi();
  const [config, setConfig] = useState(embedConfig || undefined);
  const [logo, setLogo] = useState<any | undefined>();

  useEffect(() => {
    if (!embedConfig) {
      get(`/agents/${draftAgentId ? draftAgentId : agentId}/config`)
        .then(({ data }) => {
          setLogo(data.logo_url ? data.logo_url : undefined);
          setConfig(data);
        })
        .catch(error => {
          console.error('Error getting agent config:', error);
        });
    } else {
      setLogo(embedConfig.logo_url ? embedConfig.logo_url : undefined);
      setConfig(embedConfig);
    }
  }, [embedConfig, agentId, draftAgentId, get]);

  return { config, logo };
}

export function useIpAddress() {
  const [ipAddress, setIpAddress] = useState('');

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(data => setIpAddress(data.ip))
      .catch(error => console.error('Error fetching IP address:', error));
  }, []);

  return ipAddress;
}

export function useOnlineListener(initializeWebSocket: () => void) {
  useEffect(() => {
    const handleOnline = () => {
      if (initializeWebSocket) {
        initializeWebSocket();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [initializeWebSocket]);
}

export function useActionResultsListener(
  sendMessage: (msg: string, type?: string, leadData?: any) => Promise<void>
) {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'aimdocActionResult') {
        const { results } = event.data;
        if (document.hidden) {
          console.log('Tab inactive or execution stopped - ignoring action result');
          return;
        }
        if (!results || !Array.isArray(results)) {
          console.error('Invalid action results received:', event.data);
          return;
        }
        window.parent.postMessage({
          type: 'aimdocClearActionResults',
        }, '*');
        const formattedResults = results.map((result: any) => result.message).join('\n\n');
        console.log('Action results:', formattedResults);

        sendMessage(formattedResults, 'system', undefined);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendMessage]);
}

export function useStartTaskListener() {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'startTask') {
        const { taskId, pageContext, userContext } = event.data.data;
        console.log('Start task received:', taskId, pageContext, userContext);
        // Process task info as needed
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
}

export function useInitialMessageSender(
  sessionInitialized: boolean,
  messageInput: { role: string; content: string } | undefined,
  sendMessage: (msg: string, type?: string) => Promise<void>,
  setMessageInput: (value: null) => void,
  conversationId: string | null,
  socketRef: React.MutableRefObject<WebSocket | null>
) {
  const messageProcessedRef = useRef(false);

  useEffect(() => {
    if (sessionInitialized && messageInput && !messageProcessedRef.current) {
      messageProcessedRef.current = true;

      const sendInitialMessage = async () => {
        try {
          if (conversationId && (!socketRef.current || socketRef.current.readyState === WebSocket.CONNECTING)) {
            await new Promise<void>((resolve) => {
              const checkWs = setInterval(() => {
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                  clearInterval(checkWs);
                  resolve();
                }
              }, 100);
              setTimeout(() => {
                clearInterval(checkWs);
                resolve();
              }, 5000);
            });
          }

          await sendMessage(messageInput.content, messageInput.role);
        } finally {
          setMessageInput(null);
          messageProcessedRef.current = false;
        }
      };

      sendInitialMessage();
    }
  }, [sessionInitialized]);
}

export function useCleanupOnUnmount(
  socketRef: React.MutableRefObject<WebSocket | null>,
  abortControllerRef: React.MutableRefObject<AbortController>,
  setReconnectAttempts: Dispatch<SetStateAction<number>>,
  shouldReconnectRef: React.MutableRefObject<boolean>
) {
  useEffect(() => {
    return () => {
      shouldReconnectRef.current = false;
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setReconnectAttempts(0);
    };
  }, [socketRef, abortControllerRef, setReconnectAttempts, shouldReconnectRef]);
}

export function useHandBackChatEffect(
  handBackChat: boolean,
  setAgentName: Dispatch<SetStateAction<string | undefined>>,
  setShowConnectedMessage: Dispatch<SetStateAction<boolean>>,
  setHandBackChat: Dispatch<SetStateAction<boolean>>
) {
  useEffect(() => {
    if (handBackChat) {
      setAgentName(undefined);
      setShowConnectedMessage(false);
      setHandBackChat(false);
    }
  }, [handBackChat, setAgentName, setShowConnectedMessage, setHandBackChat]);
}

// hooks for EmbedChat.tsx

/* ===========================================================================
   Hook: useParentUrlListener
   Listens for window messages with type "parentUrl" and calls setParentUrl.
   =========================================================================== */
export function useParentUrlListener(setParentUrl: (url: string) => void) {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'parentUrl') {
        setParentUrl(event.data.url);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [setParentUrl]);
}

/* ===========================================================================
   Hook: useQueryParams
   Extracts query parameters from the URL and—for keys like 'aimdoc_parent_url'—
   calls setParentUrl. Returns an object with all query parameters.
   =========================================================================== */
export function useQueryParams(setParentUrl: (url: string) => void): Record<string, string> {
  const [params, setParams] = useState<Record<string, string>>({});
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.href);
    const allParams: Record<string, string> = {};
    queryParams.forEach((value, key) => {
      allParams[key] = value;
      if (key === 'aimdoc_parent_url') {
        setParentUrl(value);
      }
    });
    setParams(allParams);
  }, [setParentUrl]);
  return params;
}

/* ===========================================================================
   Hook: useModalController
   Manages modal open/close state and posts messages to the parent window.
   =========================================================================== */
export function useModalController() {
  const [isOpen, setIsOpen] = useState(false);
  const openModal = useCallback(() => {
    setIsOpen(true);
    if (localStorage.getItem('aimdocAutoOpen') === 'false') {
      localStorage.setItem('aimdocAutoOpen', 'true');
    }
    window.parent.postMessage({ type: 'widgetOpened', divType: 'chat' }, '*');
  }, []);
  const closeModal = useCallback(() => {
    window.parent.postMessage({ type: 'widgetCollapsed', divType: 'chat' }, '*');
    setIsOpen(false);
  }, []);
  return { isOpen, openModal, closeModal };
}

/* ===========================================================================
   Hook: useUserSession
   Checks for a stored user ID (or creates one) and, if found, attempts to load
   session data (conversation ID, messages, lead ID) via an API call.
   =========================================================================== */
export function useUserSession(agentId: string) {
  const { post } = useApi();
  const [visitorId, setVisitorId] = useState<string | undefined>(undefined);
  const [conversationId, setConversationId] = useState<any>(undefined);
  const [messages, setMessages] = useState<any[]>([]);
  const [leadId, setLeadId] = useState<any>(undefined);

  const checkOrSetUserId = useCallback(async () => {
    let userId = localStorage.getItem('aimdocUserId');
    if (!userId) {
      userId = uuidv4();
      localStorage.setItem('aimdocUserId', userId);
    } else {
      try {
        const { data } = await post(`/latest-session/`, {
          visitor_id: userId,
          agent_id: agentId,
        });
        setConversationId(data.id);
        setMessages(data.messages);
        // setLeadId(data?.lead?.id);
      } catch (error) {
        console.error('Error checking latest session:', error);
      }
    }
    if (userId) {
      setVisitorId(userId);
    }
    return userId;
  }, [agentId, post]);

  return { visitorId, conversationId, messages, leadId, checkOrSetUserId, setConversationId, setMessages, setLeadId };
}

/* ===========================================================================
   Hook: useInitializationData
   Listens for window messages of type "initializationData" and stores the data.
   =========================================================================== */
export interface InitializationData {
  lang: string;
  deviceInfo: {
    isMobile: boolean;
    isTablet: boolean;
    isLaptop: boolean;
    screenWidth: number;
    screenHeight: number;
  };
}

export function useInitializationData() {
  const [initData, setInitData] = useState<InitializationData | null>(null);
  useEffect(() => {
    const handleInitialization = (event: MessageEvent) => {
      if (event.data.type === 'initializationData') {
        setInitData(event.data.data);
      }
    };
    window.addEventListener('message', handleInitialization);
    return () => window.removeEventListener('message', handleInitialization);
  }, []);
  return initData;
}

/* ===========================================================================
   Hook: useEmbedAgentConfig
   Fetches the agent's configuration using an API call, loads fonts, posts
   messages to the parent window, and updates related state.
   =========================================================================== */
export function useEmbedAgentConfig(
  agentId: string,
  isDraft: boolean,
  parentUrl: string,
  initData: InitializationData | null
) {
  const { post } = useApi();
  const [config, setConfig] = useState<any>({});
  const [links, setLinks] = useState<any>(undefined);
  const [starters, setStarters] = useState<any>(undefined);
  const [welcomeMessage, setWelcomeMessage] = useState<string>('Welcome! How can I help you today?');
  const [currentPage, setCurrentPage] = useState('');
  const [currentPageOnly, setCurrentPageOnly] = useState(false);
  const [draftAgentId, setDraftAgentId] = useState<string | undefined>(undefined);
  const [aimdocReady, setAimdocReady] = useState(false);
  const [configReady, setConfigReady] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const status = isDraft ? 'draft' : 'live';
        const { data } = await post(`/agents/${agentId}/config?status=${status}`, {
          current_page: parentUrl,
          lang: initData?.lang,
          deviceInfo: initData?.deviceInfo,
        });

        window.parent.postMessage({ type: 'aimdocReady', agentType: data.type }, '*');
        setAimdocReady(true);
        console.log('Config data:', data);

        if (data.font) {
          try {
            await loadFont(data.font);
          } catch (err) {
            console.warn('Font loading failed, using fallback:', err);
          }
        }

        setConfig(data);
        setLinks(data.links);
        setStarters(data.starters);
        setWelcomeMessage(data.welcome_message);
        setCurrentPage(data.current_page);
        setCurrentPageOnly(data.current_page_only);
        if (data.draft_agent_id) {
          setDraftAgentId(data.draft_agent_id);
        }
        if (data.auto_open_enabled) {
          if (!data.open_once_per_session || localStorage.getItem('aimdocAutoOpen') !== 'true') {
            setTimeout(() => {
              window.parent.postMessage({ type: 'widgetOpened', divType: 'chat' }, '*');
              localStorage.setItem('aimdocAutoOpen', 'true');
            }, data.auto_open_seconds * 1000);
          }
        }
        setConfigReady(true);
      } catch (error) {
        console.error('Error getting agent config:', error);
      }
    };

    if (parentUrl) {
      fetchConfig();
    }
  }, [agentId, isDraft, parentUrl, initData]);

  return { config, links, starters, welcomeMessage, currentPage, currentPageOnly, draftAgentId, aimdocReady, configReady };
}

/* ===========================================================================
   Hook: useDebounce
   Returns a debounced version of a function.
   =========================================================================== */
export function useDebounce<T extends (...args: any[]) => void>(func: T, delay: number): T {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const debouncedFunc = useCallback((...args: any[]) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      func(...args);
    }, delay);
  }, [func, delay]);
  return debouncedFunc as T;
}

/* ===========================================================================
   Hook: useClickEventListener
   Listens for window messages of type "newClickEvent" and processes click events
   using a debounced callback.
   =========================================================================== */
export function useClickEventListener(debouncedProcessClickEvent: (clickData: any) => void) {
  const clickEventRef = useRef<{ lastClickedElement: string | null; lastClickTime: number }>({
    lastClickedElement: null,
    lastClickTime: 0,
  });

  useEffect(() => {
    const handleClickEvent = (event: MessageEvent) => {
      if (event.data.type === 'newClickEvent') {
        const clickData = event.data.data;
        const currentTime = Date.now();
        if (
          clickData.element_id === clickEventRef.current.lastClickedElement &&
          currentTime - clickEventRef.current.lastClickTime < 300
        ) {
          clickData.timestamp = new Date().toISOString();
        } else {
          clickEventRef.current.lastClickedElement = clickData.element_id;
          clickEventRef.current.lastClickTime = currentTime;
          debouncedProcessClickEvent(clickData);
        }
      }
    };

    window.addEventListener('message', handleClickEvent);
    return () => window.removeEventListener('message', handleClickEvent);
  }, [debouncedProcessClickEvent]);
}

/* ===========================================================================
   Hook: useClickEventsListener
   Requests stored click events from the parent and saves them into localStorage.
   =========================================================================== */
export function useClickEventsListener() {
  useEffect(() => {
    window.parent.postMessage({ type: 'getClickEvents' }, '*');
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'clickEvents') {
        localStorage.setItem('aimdocEvents', JSON.stringify(event.data.data));
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
}

/* ===========================================================================
   Hook: useContentResizeObserver
   Observes the height of a content element and notifies the parent to adjust
   the iframe height accordingly.
   =========================================================================== */
export function useContentResizeObserver(contentRef: RefObject<HTMLDivElement>, isOpen: boolean) {
  useEffect(() => {
    if (!contentRef.current) return;
    const contentElement = contentRef.current;
    let resizeTimeout: NodeJS.Timeout;
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const height = entry.contentRect.height;
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          if (!isOpen) {
            const adjustedHeight = height + 40;
            window.parent.postMessage({ type: 'setIframeHeight', divType: 'chat', height: adjustedHeight }, '*');
          }
        }, 110);
      }
    });
    resizeObserver.observe(contentElement);
    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [contentRef, isOpen]);
}

/* ===========================================================================
   Hook: useCustomStyle
   Injects custom CSS (e.g. for a rotating animation using a custom property)
   into the document head.
   =========================================================================== */
export function useCustomStyle(configColor: string) {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @property --angle {
        syntax: '<angle>';
        initial-value: 0deg;
        inherits: false;
      }
      @keyframes rotate {
        from { --angle: 0deg; }
        to { --angle: 360deg; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [configColor]);
}

/* ===========================================================================
   Hook: useStoredActionResultsHandler
   Listens for stored action results sent via window messages and, if both
   aimdocReady and configReady are true, sets a system message and opens the
   modal.
   =========================================================================== */
export function useStoredActionResultsHandler(
  aimdocReady: boolean,
  configReady: boolean,
  openModal: () => void,
  setMessage: (msg: { role: string; content: string }) => void
) {
  useEffect(() => {
    const handleStoredResults = (event: MessageEvent) => {
      if (event.data.type === 'aimdocStoredActionResults') {
        const storedResults = event.data.data;
        if (storedResults && storedResults.length > 0) {
          const formattedResults = storedResults.map((result: any) => result.message).join('\n\n');
          if (aimdocReady && configReady) {
            setMessage({ role: 'system', content: formattedResults });
            openModal();
          }
        }
      }
    };

    if (aimdocReady && configReady) {
      window.addEventListener('message', handleStoredResults);
      window.parent.postMessage({ type: 'aimdocGetActionResults' }, '*');
    }
    return () => window.removeEventListener('message', handleStoredResults);
  }, [aimdocReady, configReady, openModal, setMessage]);
}